import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PublicLayout from '@/components/PublicLayout';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Handshake, Send, CheckCircle2, Ticket, Star, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { trackEvent } from '@/components/GA4';

const EMPTY = { nome_locale: '', referente: '', email: '', telefono: '', tipo_richiesta: 'Vendita Biglietti in Affiliazione', messaggio: '' };

export default function Collabora() {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome_locale || !form.referente || !form.email || !form.telefono || !form.messaggio) {
      toast.error('Compila tutti i campi'); return;
    }
    setBusy(true);
    try {
      await api.post('/public/collaborazioni', form);
      trackEvent('collabora_submit', { tipo: form.tipo_richiesta });
      setDone(true);
      toast.success(t('collabora.success'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore di invio');
    } finally { setBusy(false); }
  };

  return (
    <PublicLayout>
      <SEO
        title={`${t('collabora.title')} — Roma Nightlife`}
        description="Vendi i biglietti dei tuoi eventi e metti il tuo locale in prima pagina su Roma Nightlife. Contattaci per una collaborazione B2B."
        canonical={typeof window !== 'undefined' ? window.location.href : ''}
      />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16 hero-overlay relative">
        <div className="relative z-10 max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase text-[color:var(--gold)] mb-3 flex items-center gap-2"><Handshake className="w-4 h-4"/>B2B</p>
          <h1 data-testid="collabora-title" className="font-heading text-4xl md:text-5xl mb-4">{t('collabora.title')}</h1>
          <p className="text-lg text-[color:var(--text-muted)] leading-relaxed">{t('collabora.intro')}</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-3 gap-3 mb-10">
        <Card icon={Ticket} title="Vendita Biglietti" text="Vendi i biglietti dei tuoi eventi tramite il nostro circuito di affiliazione." />
        <Card icon={Star} title="Evento in Evidenza" text="Il tuo evento in cima all'home page e nelle pagine di zona." />
        <Card icon={MessageSquare} title="Altro" text="Hai un'idea diversa? Raccontaci come possiamo collaborare." />
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16" data-testid="collabora-form-section">
        <div className="rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-6 md:p-8">
          <h2 className="font-heading text-2xl mb-4 text-[color:var(--gold)]">{t('collabora.formTitle')}</h2>
          {done ? (
            <div className="text-center py-8" data-testid="collabora-success">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-3"/>
              <h3 className="font-heading text-xl mb-2">{t('collabora.success')}</h3>
              <Link to="/"><Button variant="outline" className="border-[color:var(--gold)] text-[color:var(--gold)] mt-2">{t('common.backHome')}</Button></Link>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid="collabora-form">
              <Field label={`${t('collabora.nomeLocale')} *`}><Input data-testid="collabora-nome-locale" value={form.nome_locale} onChange={(e) => setForm({...form, nome_locale: e.target.value})} required className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label={`${t('collabora.referente')} *`}><Input data-testid="collabora-referente" value={form.referente} onChange={(e) => setForm({...form, referente: e.target.value})} required className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label={`${t('collabora.email')} *`}><Input data-testid="collabora-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label={`${t('collabora.telefono')} *`}><Input data-testid="collabora-telefono" value={form.telefono} onChange={(e) => setForm({...form, telefono: e.target.value})} required className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <div className="sm:col-span-2">
                <Field label={`${t('collabora.tipoRichiesta')} *`}>
                  <Select value={form.tipo_richiesta} onValueChange={(v) => setForm({...form, tipo_richiesta: v})}>
                    <SelectTrigger data-testid="collabora-tipo" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"><SelectValue/></SelectTrigger>
                    <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border-c)]">
                      <SelectItem value="Vendita Biglietti in Affiliazione">{t('collabora.types.vendita')}</SelectItem>
                      <SelectItem value="Mettere Evento in Evidenza">{t('collabora.types.evidenza')}</SelectItem>
                      <SelectItem value="Altro">{t('collabora.types.altro')}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label={`${t('collabora.messaggio')} *`}><Textarea data-testid="collabora-messaggio" value={form.messaggio} onChange={(e) => setForm({...form, messaggio: e.target.value})} required rows={5} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              </div>
              <div className="sm:col-span-2">
                <Button data-testid="collabora-submit" type="submit" disabled={busy} className="w-full bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black font-semibold btn-press"><Send className="w-4 h-4 mr-2"/>{busy ? 'Invio…' : t('collabora.submit')}</Button>
              </div>
            </form>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}

function Card({ icon: Icon, title, text }) {
  return (
    <div className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-5">
      <Icon className="w-6 h-6 text-[color:var(--gold)] mb-2"/>
      <h3 className="font-heading text-lg mb-1">{title}</h3>
      <p className="text-sm text-[color:var(--text-muted)]">{text}</p>
    </div>
  );
}
function Field({ label, children }) {
  return (<div><Label className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1 block">{label}</Label>{children}</div>);
}
